import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {FaEdit, FaEye, FaTrashAlt} from 'react-icons/fa';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import styles from './ListaUnidadeConsumidora.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';
import { UserContext } from '../../UserContext';
import Pagination from '../pagination/Pagination';
import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';

function ListaUnidadeConsumidora() {
    const { user, setUser } = useContext(UserContext);
    const { get, del } = useHttpClient();
    const { exibirErro, exibirSucesso } = useNotificacoes();

    const [unidades, setUnidades] = useState([]);
    const [usinas, setUsinas] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [filtros, setFiltros] = useState({ idUsina: '', idCliente: '', nomeUnidade: '', codigoCliente: '' });
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const jaCarregou = useRef(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!user && localStorage.getItem('userData')) {
            setUser(JSON.parse(localStorage.getItem('userData')));
        }
    }, [user, setUser]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const resp = await get(`/hello-sol/usinas/buscar?idEmpresa=${user.empresa.id}`);
                setUsinas(Array.isArray(resp) ? resp : resp.content ?? []);
            } catch (err) {
                exibirErro('Erro ao buscar usinas.');
            }
        })();
    }, [user]);

    // Carregar clientes ao mudar a usina
    useEffect(() => {
        if (!filtros.idUsina) {
            setClientes([]);
            return;
        }

        (async () => {
            try {
                const resp = await get(`/hello-sol/usuarios/buscarUsuarioUsina?` +
                    new URLSearchParams({
                        idUsina: filtros.idUsina,
                        idTipoUsuario: 3 // Cliente
                    }).toString());
                setClientes(Array.isArray(resp) ? resp : (resp.content ?? []));
            } catch {
                exibirErro('Erro ao buscar clientes da usina.');
            }
        })();
    }, [filtros.idUsina]);

    const fetchUnidades = useCallback(async (usarFiltros = false, page = 0) => {
        if (!user?.empresa?.id || !filtros.idUsina) return;

        setLoading(true);
        try {
            const params = new URLSearchParams();

            params.append('idEmpresa', user.empresa.id);
            params.append('idUsina', filtros.idUsina);
            params.append('page', page);
            params.append('pageSize', pageSize);

            if (usarFiltros) {
                if (filtros.idCliente && !isNaN(Number(filtros.idCliente))) {
                    params.append('idCliente', filtros.idCliente);
                }

                if (filtros.nomeUnidade?.trim()) {
                    params.append('nomeUnidade', filtros.nomeUnidade.trim());
                }

                if (filtros.codigoCliente?.trim()) {
                    params.append('codigoCliente', filtros.codigoCliente.trim());
                }
            }

            const data = await get(`/hello-sol/unidades-consumidoras?${params.toString()}`);
            setUnidades(Array.isArray(data.content) ? data.content : []);
            setTotalPages(data.totalPages || 0);
        } catch {
            exibirErro('Erro ao buscar unidades consumidoras.');
        } finally {
            setLoading(false);
        }
    }, [get, filtros, user?.empresa?.id, pageSize]);



    useEffect(() => {
        if (!jaCarregou.current && user?.empresa?.id) {
            jaCarregou.current = true;
            fetchUnidades(false, 0);
        }
    }, [user?.empresa?.id, fetchUnidades]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        fetchUnidades(true, 0);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            fetchUnidades(true, newPage);
        }
    };

    const handleDelete = async (id) => {
        try {
            await del(`/hello-sol/unidades-consumidoras/${id}`);
            exibirSucesso('Unidade excluída com sucesso.');
            fetchUnidades(true, currentPage);
        } catch {
            exibirErro('Erro ao excluir a unidade.');
        }
    };

    const confirmDelete = (id) => {
        confirmAlert({
            title: 'Confirmação',
            message: 'Deseja realmente excluir esta unidade?',
            buttons: [
                { label: 'Sim', onClick: () => handleDelete(id) },
                { label: 'Não' }
            ]
        });
    };

    return (
        <div className={styles_global.container}>
            <h2>Consultar Unidades Consumidoras</h2>

            <form className={styles.filterForm} onSubmit={handleSubmit}>
                <div className={`${styles_content.formField} ${styles.campoUsina}`}>
                    <label>Usina</label>
                    <select name="idUsina" value={filtros.idUsina} onChange={handleChange}
                            className={styles.campoUsina} required >
                        <option value="">Selecione a Usina</option>
                        {usinas.map(usina => (
                            <option key={usina.id} value={usina.id}>{usina.nome}</option>
                        ))}

                    </select>
                </div>

                <div className={`${styles_content.formField} ${styles.campoCliente}`}>
                    <label>Cliente</label>
                    <select name="idCliente" value={filtros.idCliente} onChange={handleChange}
                            className={styles.campoCliente}
                    >
                        <option value="">Todos os Clientes</option>
                        {clientes.map(cliente => (
                            <option key={cliente.id} value={cliente.id}>
                                {cliente.nome}{cliente.cadastroPendente ? ' (pendente)' : ''}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={`${styles_content.formField} ${styles.campoCodCliente}`}>
                    <label>Código do Cliente (UC)</label>
                    <input
                        name="codigoCliente"
                        className={styles.campoCodCliente}
                        value={filtros.codigoCliente}
                        onChange={handleChange}
                        placeholder="Ex: 2.665.591-8"
                    />
                </div>

                    <div className={`${styles_content.formField} ${styles.campoNome}`}>
                        <label>Nome da Unidade</label>
                        <input
                            name="nomeUnidade"
                            className={styles.campoNome}
                            value={filtros.nomeUnidade}
                            onChange={handleChange}
                            placeholder="Ex: Bloco A"
                        />
                    </div>

                    <div className={styles.botoesAbaixo}>
                        <button type="submit" disabled={loading}>
                            {loading ? 'Consultando...' : 'Consultar'}
                        </button>
                        <button type="button" onClick={() => navigate('/home/cadastrar-unidade-consumidora')}>
                            Nova Unidade
                        </button>
                    </div>
            </form>

            {loading && <p className={styles_global.loading}>Carregando...</p>}

            {!loading && unidades.length > 0 && (
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                        <tr>
                            <th>USINA</th>
                            <th>CLIENTE</th>
                            <th>CÓD. UC</th>
                            <th>NOME</th>
                            <th>AÇÕES</th>
                        </tr>
                        </thead>
                        <tbody>
                        {unidades.map((uni) => (
                            <tr key={uni.id}>
                                <td>{uni.cliente.usina.nome || '—'}</td>
                                <td>
                                    {uni.cliente.nome || '—'}
                                    {uni.cliente.cadastroPendente && (
                                        <div style={{ fontSize: '12px', color: '#8a5a00', marginTop: '4px' }}>
                                            Cliente pendente de revisão
                                        </div>
                                    )}
                                </td>
                                <td>{uni.codCliente || '—'}</td>
                                <td>{uni.nomeUnidade || '—'}</td>
                                <td className={styles.actions}>
                                    <Link to={`/home/detalhar-unidade-consumidora/${uni.id}`}
                                          className={styles.iconButton}>
                                        <FaEye className={styles.icon}/>
                                    </Link>
                                    <Link to={`/home/cadastrar-unidade-consumidora/${uni.id}`}
                                          className={styles.iconButton}>
                                        <FaEdit className={styles.icon}/>
                                    </Link>
                                    <Link to="#" onClick={() => confirmDelete(uni.id)} className={styles.iconButton}>
                                        <FaTrashAlt className={`${styles.icon} ${styles.delete}`}/>
                                    </Link>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                </div>
            )}

            {!loading && unidades.length === 0 && (
                <p className={styles_global.noData}>Nenhuma unidade encontrada.</p>
            )}
        </div>
    );
}

export default ListaUnidadeConsumidora;
